import { RouteType } from "types/RouteType";
import { RootStore } from "@intrasync/stores";
import MMProductionStore from "stores/millermize/MMProductionStore";
import MMLoadStore from "stores/millermize/MMLoadStore";

const productionRoute: RouteType = {
  name: "MM Production",
  path: "/mm/production",
  layout: "/app",
  viewportCard: {
    header: {
      title: "Production Schedule",
      component: () => import("./Production.Header"),
      enableRefreshButton: true,
      enableBackButton: false,
    },
    body: {
      component: () => import("./Production.Body"),
      grayBackground: true,
    },
  },
  group: "Miller-Mize",
  menuOrder: 4,
  requiresAuth: true,
  requiresPermission: false,
  dialogs: [
    {
      name: "Add Mark",
      title: "Add Production Mark",
      height: 520,
      component: () => import("./ProductionDialogs/MarkFormDialog"),
    },
    {
      name: "Edit Mark",
      title: "Edit Production Mark",
      height: 620,
      component: () => import("./ProductionDialogs/MarkFormDialog"),
    },
    {
      name: "Paste Marks",
      title: "Paste Marks from Excel",
      height: 640,
      component: () => import("./ProductionDialogs/BulkMarkDialog"),
    },
    {
      // Same component, the job-wide entry point. The marks land in Staging
      // rather than in a group, because a packet of tickets is a JOB's
      // paperwork -- Creede sorts it into groups after he has seen it.
      name: "Import Tickets",
      title: "Import Production Tickets (PDF)",
      height: 640,
      component: () => import("./ProductionDialogs/BulkMarkDialog"),
    },
    {
      // ONE note action for all three scopes. dialogContext addresses it as
      // "scope:id" -- e.g. "mark:<uuid>" from a mark's row -- so the common
      // case opens on the right target and the selector is the escape hatch.
      name: "Add Note",
      title: "Notes",
      height: 600,
      component: () => import("./ProductionDialogs/NoteDialog"),
    },
    {
      name: "Add Group",
      title: "Add Ticket Group",
      component: () => import("./ProductionDialogs/TicketGroupDialog"),
    },
    {
      // Same component as "Add Group", and deliberately so: a phase IS a ticket
      // group that names one, which is how Creede described it. But the two
      // buttons opening a dialog titled "Add Ticket Group" made the Add Phase
      // one look like a misfire -- the model being shared is our business, not
      // his. Its own title, and the component leads with the phase field.
      name: "Add Phase",
      title: "Add Phase",
      component: () => import("./ProductionDialogs/TicketGroupDialog"),
    },
    {
      name: "Edit Group",
      title: "Edit Ticket Group",
      component: () => import("./ProductionDialogs/TicketGroupDialog"),
    },
    {
      // Forms are created/managed in the "Mold Shop Forms" section; ticket groups
      // just assign existing forms.
      name: "Add Form",
      title: "Add Form",
      component: () => import("./ProductionDialogs/FormDialog"),
    },
    {
      name: "Edit Form",
      title: "Edit Form",
      component: () => import("./ProductionDialogs/FormDialog"),
    },
  ],
  /**
   * Re-read on entry, and on the header's refresh button (item 18).
   *
   * Creede's "slow refresh": a load marked delivered in one place did not show
   * in another. No mm-* service emits real-time events and each store loaded
   * exactly once per tab, so nothing ever re-read. Coming back to the screen now
   * does.
   *
   * `manualTrigger` is true only for the refresh button (RefreshDataButton calls
   * `triggerEnterSideEffects(true)`), which is what lets a deliberate refresh
   * bypass the throttle that keeps ordinary navigation cheap.
   *
   * Fire-and-forget, and never blocking: the bodies render their own loading
   * state, and a failed re-read must not keep him off the screen. Cast follows
   * the established route-hook pattern -- MM stores are registered dynamically,
   * so RootStore types them loosely.
   */
  beforeEnter: async ({
    rootStore,
    manualTrigger,
  }: {
    rootStore: RootStore;
    manualTrigger?: boolean;
  }) => {
    const production = rootStore.mmProductionStore as
      | MMProductionStore
      | undefined;
    const loads = rootStore.mmLoadStore as MMLoadStore | undefined;
    // Both, on both screens: delivered piece counts are derived from loads and
    // displayed against marks, so reading one without the other is what made
    // the two disagree in the first place.
    void production?.refresh({ force: !!manualTrigger });
    void loads?.refresh({ force: !!manualTrigger });
    return true;
  },
};

export default productionRoute;
